import React, { useEffect, useRef, useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Btn from '../../../components/ui/Btn';
import Field, { TextInput, NativeSelect } from '../../../components/ui/Field';
import { engineService } from '../../../services/engineService';
import { CONTEXT_KEYS } from '../../../utils/toolDefinition';
import { agentsService } from '../../../services/agentsService';
import { isExternal } from '../../../utils/agentReadiness';

// Lo que se escribió en la última prueba de cada herramienta se recuerda en este navegador: no hay que volver a capturarlo.
const memKey = (id) => `tool-test-${id}`;
const readMem = (id) => { try { return JSON.parse(localStorage.getItem(memKey(id)) || '{}'); } catch { return {}; } };
const writeMem = (id, v) => { try { localStorage.setItem(memKey(id), JSON.stringify(v)); } catch { /* sin almacenamiento */ } };
const BASE_CTX = ['phone', 'name', 'channel'];

const ms = (n) => (n < 1000 ? `${n} ms` : `${(n / 1000).toFixed(1)} s`);

// Probar el BORRADOR con datos de ejemplo. Corre de verdad (inserta, llama la API): por eso se avisa antes y se pide confirmar.
export default function TestPanel({ open, onClose, draft, sample, onResult, toolId, pinned, defaults = {}, scope }) {
  const pinCount = Object.keys(pinned || {}).length;
  const [usePins, setUsePins] = useState(true);
  const ai = (draft.inputs || []).filter((i) => i.source === 'ai');
  const agentSlots = (draft.connectors || []).filter((s) => s.mode === 'agent');
  const [values, setValues] = useState(() => readMem(toolId).values || {});
  const [ctx, setCtx] = useState(() => ({ phone: '5215500000000', name: 'Persona de prueba', channel: 'whatsapp', chat_id: 'prueba-1', contact_id: '1', ...defaults, ...(readMem(toolId).ctx || {}) }));
  // Datos de la conversación que esta herramienta usa (teléfono, id del proyecto…): se piden aquí para que las pruebas no corran con vacíos.
  const ctxKeys = [...new Set([...BASE_CTX, ...(draft.inputs || []).filter((i) => i.source === 'context' && !['now', 'date'].includes(i.context)).map((i) => i.context)])];
  const extraKeys = ctxKeys.filter((k) => !BASE_CTX.includes(k));
  // Los que el sistema conoce (agente y proyecto de una herramienta personal) entran solos si todavía no hay valor.
  useEffect(() => { setCtx((c) => ({ ...Object.fromEntries(Object.entries(defaults).filter(([k, v]) => v != null && !c[k])), ...c })); }, [defaults.agent_id, defaults.project_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  const ctrl = useRef(null);
  useEffect(() => { if (open) { setRes(null); setError(''); } }, [open]);
  // Herramienta global: no tiene agente propio, así que se elige con qué agente probar (solo para probar; en uso real viene de la conversación).
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    if (!open || scope !== 'global') return;
    agentsService.listAgents().then((a) => setAgents(a.filter((x) => !isExternal(x) && x.status !== 'archived'))).catch(() => {});
  }, [open, scope]);
  const pickAgent = (id) => {
    const a = agents.find((x) => String(x.id) === id);
    setCtx((c) => ({ ...c, agent_id: a ? String(a.id) : '', project_id: a?.project_id != null ? String(a.project_id) : '' }));
  };

  const run = async () => {
    writeMem(toolId, { values, ctx });
    setBusy(true); setError(''); setRes(null);
    ctrl.current = new AbortController();
    try {
      const inputs = Object.fromEntries(ai.map((i) => [i.key, i.type === 'boolean' ? values[i.key] === 'true' : values[i.key] ?? '']));
      const r = await engineService.testTool({ toolId, definition: draft, connectors: Object.fromEntries(agentSlots.map((s) => [s.slot, sample[s.slot]])), inputs, context: ctx, pinned: usePins ? pinned : undefined }, { signal: ctrl.current.signal });
      setRes(r); onResult(r);
    } catch (e) { if (e.code !== 'aborted') setError(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={busy ? undefined : onClose} title="Probar la herramienta" width="max-w-2xl"
      description="Se ejecuta el borrador de verdad con estos datos: si guarda un lead, el lead se guarda. Usa datos que se reconozcan como prueba."
      footer={<><Btn variant="ghost" onClick={busy ? () => ctrl.current?.abort() : onClose}>{busy ? 'Cancelar' : 'Cerrar'}</Btn><Btn variant="primary" onClick={run} loading={busy}>Ejecutar prueba</Btn></>}>
      <div className="space-y-5">
        {ai.length > 0 && (
          <fieldset className="space-y-3">
            <legend className="text-[13px] font-semibold text-fg mb-1">Lo que mandaría la IA</legend>
            {ai.map((i) => (
              <Field key={i.key} label={i.key} hint={i.description} optional={i.required === false}>
                {i.type === 'enum' ? (
                  <NativeSelect value={values[i.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [i.key]: e.target.value }))}><option value="">—</option>{(i.options || []).map((o) => <option key={o}>{o}</option>)}</NativeSelect>
                ) : i.type === 'boolean' ? (
                  <NativeSelect value={values[i.key] ?? 'true'} onChange={(e) => setValues((v) => ({ ...v, [i.key]: e.target.value }))}><option value="true">Sí</option><option value="false">No</option></NativeSelect>
                ) : (
                  <TextInput value={values[i.key] ?? ''} onChange={(e) => setValues((v) => ({ ...v, [i.key]: e.target.value }))} inputMode={i.type === 'string' ? undefined : 'decimal'} />
                )}
              </Field>
            ))}
          </fieldset>
        )}
        <fieldset>
          <legend className="text-[13px] font-semibold text-fg mb-1">Conversación de ejemplo</legend>
          <p className="mb-2 text-[12.5px] text-fg-faint">Lo que el sistema pone solo en una conversación real. Aquí lo escribes para probar; se recuerda para la próxima vez.</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Teléfono"><TextInput value={ctx.phone} onChange={(e) => setCtx((c) => ({ ...c, phone: e.target.value }))} /></Field>
            <Field label="Nombre"><TextInput value={ctx.name} onChange={(e) => setCtx((c) => ({ ...c, name: e.target.value }))} /></Field>
            <Field label="Canal"><NativeSelect value={ctx.channel} onChange={(e) => setCtx((c) => ({ ...c, channel: e.target.value }))}><option value="whatsapp">WhatsApp</option><option value="telegram">Telegram</option><option value="web">Chat web</option></NativeSelect></Field>
          </div>
          {scope === 'global' && (
            <div className="mt-3">
              <Field label="Probar como el agente" hint="Pone el id del agente y el de su proyecto en la prueba. Solo sirve para probar: al usarla de verdad, salen de la conversación.">
                <NativeSelect value={ctx.agent_id && agents.some((a) => String(a.id) === String(ctx.agent_id)) ? String(ctx.agent_id) : ''} onChange={(e) => pickAgent(e.target.value)}>
                  <option value="">Ninguno (escribir los ids a mano)</option>
                  {agents.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.project?.name || `proyecto ${a.project_id}`}</option>)}
                </NativeSelect>
              </Field>
            </div>
          )}
          {extraKeys.length > 0 && (
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              {extraKeys.map((k) => (
                <Field key={k} label={CONTEXT_KEYS.find((c) => c.key === k)?.label || k}><TextInput value={ctx[k] ?? ''} onChange={(e) => setCtx((c) => ({ ...c, [k]: e.target.value }))} spellCheck={false} /></Field>
              ))}
            </div>
          )}
        </fieldset>
        {pinCount > 0 && (
          <label className="flex items-center gap-2 text-[13px] text-fg-dim">
            <input type="checkbox" checked={usePins} onChange={(e) => setUsePins(e.target.checked)} className="h-4 w-4 accent-[rgb(var(--live))]" />
            Usar los datos fijados ({pinCount} {pinCount === 1 ? 'paso' : 'pasos'}): esos pasos no se ejecutan.
          </label>
        )}
        {agentSlots.some((s) => !sample[s.slot]) && <p role="note" className="text-[13px] text-caution">Elige un conector de prueba para «{agentSlots.find((s) => !sample[s.slot]).label || agentSlots.find((s) => !sample[s.slot]).slot}» en «Cuando el agente la usa».</p>}
        {error && <p role="alert" className="text-[13px] text-signal">{error}</p>}
        {res && (
          <div className="space-y-3" role="status">
            <div className={`rounded-xl px-4 py-3 text-[13.5px] ${res.ok ? 'bg-live-soft text-fg' : 'bg-signal-soft text-fg'}`}>
              <p className="text-[12px] font-semibold text-fg-dim mb-1">{res.ok ? 'Funcionó. El agente recibiría:' : 'Falló. El agente recibiría:'}</p>
              <p className="whitespace-pre-wrap break-words">{res.output}</p>
            </div>
            <ol className="space-y-2">
              {(res.nodes || []).map((n) => (
                <li key={n.id} className="rounded-xl ring-1 ring-line p-3">
                  <p className="flex flex-wrap items-center gap-2 text-[13px]">
                    <span className={`h-2 w-2 rounded-full ${n.ok ? 'bg-live' : 'bg-signal'}`} aria-hidden />
                    <span className="font-semibold text-fg">{n.name}</span>
                    <span className="text-fg-faint tabular-nums">{ms(n.ms)}{n.status ? ` · ${n.status}` : ''}</span>
                  </p>
                  {n.request && <p className="mt-1.5 text-[12px] font-mono text-fg-dim break-all">{n.request.method} {n.request.url}</p>}
                  {n.request?.body && <pre className="mt-1.5 max-h-40 overflow-auto rounded-md bg-ink p-2 text-[11.5px] text-fg-dim whitespace-pre-wrap break-words">{n.request.body}</pre>}
                  {n.result && <pre className="mt-1.5 max-h-40 overflow-auto rounded-md bg-ink p-2 text-[11.5px] text-fg-dim whitespace-pre-wrap break-words">{n.result}</pre>}
                  {n.error && <p className="mt-1.5 text-[12.5px] text-signal">{n.error}</p>}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </Modal>
  );
}
